import styled from 'styled-components';
import { theme } from 'styled-tools';

import BadgeIcon from '@ui/badgeIcon';
import Box from '@ui/box/Box.tsx';
import { Icon } from '@ui/icons';
import VisuallyHidden from '@ui/visuallyHidden';

import DestinationIcon from './DestinationIcon.tsx';
import RouteStepIcon from './RouteStepIcon.tsx';
import type { RouteLegKind, RouteSegment, Translate } from './types.ts';

export type RouteSegmentsProps = {
  T: Translate;
  segments: readonly RouteSegment[];
  destinationIcon?: string;
};

const LEG_COLOR_TOKENS: Record<RouteLegKind, string> = {
  walk: 'colors.primary',
  train: 'colors.categoryBadgeWayfindingTrain',
  bus: 'colors.categoryBadgeWayfindingBus',
  security: 'colors.categoryBadgeSecurity',
};

const LEG_ICONS: Record<RouteLegKind, string> = {
  walk: 'walking',
  train: 'wayfinding.train',
  bus: 'wayfinding.bus',
  security: 'security',
};

const LEG_LABEL_KEYS: Record<RouteLegKind, string> = {
  walk: 'getDirectionsFromTo:Walking duration _duration_',
  train: 'getDirectionsFromTo:Train ride duration _duration_',
  bus: 'getDirectionsFromTo:Bus ride duration _duration_',
  security: 'getDirectionsFromTo:Security wait duration _duration_',
};

// Reserve the painted glyph footprint rather than its padded SVG box; the 28px glyph overflows the slot evenly.
const LEG_ICON_SLOT = { width: 12, height: 20, textGap: 6, glyphSize: 28 };
const LEG_BADGE_SIZE = 20;
// Fixed rather than themed so stop numbers keep the design's size in every theme.
const STOP_FONT = { size: 16, lineHeight: 24 };
const SEGMENTS_TOP_GAP = 12;
const LEG_SPACING = 8;

const Segments = styled(Box)`
  width: 100%;
  margin: ${SEGMENTS_TOP_GAP}px 0 0;
  padding: 0;
  list-style: none;
  font-family: ${theme('fonts.family')};
`;

const SegmentItem = styled.li`
  position: relative;
  display: inline-flex;
  flex: none;
  align-items: center;

  &:not(:last-child)::after {
    position: absolute;
    inset-inline-end: -${LEG_SPACING}px;
    inset-block-start: calc(50% - ${theme('borderWidths.sm')});
    width: ${LEG_SPACING}px;
    height: ${theme('borderWidths.md')};
    background-color: ${theme('colors.secondaryButtonHover')};
    content: '';
  }
`;

const OriginMarker = styled(BadgeIcon).attrs(({ theme: currentTheme }) => ({
  borderColor: currentTheme.colors.miscBadgeIconStroke,
}))``;

// Figma's stop ring is slightly thicker than the 2px theme border.
const STOP_RING_WIDTH = 2.29;

/** Shared with the intermediate stop rows in the directions layer. */
export const StopMarker = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  width: ${theme('iconSizes.md')}px;
  height: ${theme('iconSizes.md')}px;
  box-shadow: inset 0 0 0 ${STOP_RING_WIDTH}px ${theme('colors.primary')};
  border-radius: ${theme('radii.full')};
  color: ${theme('colors.primary')};
  background-color: ${theme('colors.background')};
  font-size: ${STOP_FONT.size}px;
  font-weight: ${theme('fontWeights.bold')};
  line-height: ${STOP_FONT.lineHeight}px;
`;

const Leg = styled(Box)<{ $kind: RouteLegKind }>`
  height: ${theme('iconSizes.md')}px;
  gap: ${LEG_ICON_SLOT.textGap}px;
  border: ${theme('borderWidths.md')} solid ${props => theme(LEG_COLOR_TOKENS[props.$kind])(props)};
  border-radius: ${theme('radii.full')};
  color: ${theme('colors.primaryText')};
  font-size: ${theme('fonts.size.h4')};
  font-weight: ${theme('fontWeights.lighter')};
  line-height: 1.5;
  white-space: nowrap;
`;

const LegIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: none;
  width: ${LEG_ICON_SLOT.width}px;
  height: ${LEG_ICON_SLOT.height}px;
  color: ${theme('colors.primary')};

  svg {
    flex: none;
  }
`;

export function numberStops(segments: readonly RouteSegment[]) {
  let stop = 0;
  return segments.map(segment => ({ segment, stop: segment.kind === 'stop' ? ++stop : 0 }));
}

export default function RouteSegments({ T, segments, destinationIcon }: RouteSegmentsProps) {
  return (
    <Segments as="ol" role="list" display="flex" flexWrap="wrap" alignItems="center" gridGap={2}>
      <SegmentItem aria-hidden="true">
        <OriginMarker name="wayfinding.start" />
      </SegmentItem>
      {numberStops(segments).map(({ segment, stop }, index) => (
        <SegmentItem key={index}>
          <VisuallyHidden>
            {segment.kind === 'stop'
              ? T('getDirectionsFromTo:Stop _stop_', { stop })
              : T(LEG_LABEL_KEYS[segment.kind], { duration: segment.duration })}
          </VisuallyHidden>
          {segment.kind === 'stop' ? (
            <StopMarker aria-hidden="true">{stop}</StopMarker>
          ) : (
            <Leg as="span" $kind={segment.kind} display="inline-flex" alignItems="center" px={2} aria-hidden="true">
              {segment.kind === 'walk' ? (
                <LegIcon>
                  <Icon name={LEG_ICONS.walk} iconSize={LEG_ICON_SLOT.glyphSize} />
                </LegIcon>
              ) : (
                <RouteStepIcon name={LEG_ICONS[segment.kind]} size={LEG_BADGE_SIZE} />
              )}
              <span>{segment.duration}</span>
            </Leg>
          )}
        </SegmentItem>
      ))}
      <SegmentItem aria-hidden="true">
        <DestinationIcon name={destinationIcon} />
      </SegmentItem>
    </Segments>
  );
}
